import { getGraspActions } from "../graspStore";
import type { SceneCamera } from "../../types/scene";

type CleanupMode = "remove" | "downgrade";

type CleanupDraft = {
  cameras?: Record<string, SceneCamera>;
};

type CleanupOptions = {
  draft?: CleanupDraft;
  deletedBodyUuids?: Iterable<string>;
  deletedSiteUuids?: Iterable<string>;
  cameras?: {
    mode?: CleanupMode;
  };
  external?: {
    grasps?: boolean;
  };
};

const EMPTY_SET = new Set<string>();

/**
 * Shared referential cleanup for scene entities that can point at deleted targets.
 * New domains (lights, sensors, etc.) should be added here.
 */
export function cleanupReferencesForDeletedTargets({
  draft,
  deletedBodyUuids = EMPTY_SET,
  deletedSiteUuids = EMPTY_SET,
  cameras,
  external,
}: CleanupOptions): void {
  const deletedBodies =
    deletedBodyUuids instanceof Set ? deletedBodyUuids : new Set(deletedBodyUuids);
  const deletedSites =
    deletedSiteUuids instanceof Set ? deletedSiteUuids : new Set(deletedSiteUuids);
  if (deletedBodies.size === 0 && deletedSites.size === 0) return;

  if (cameras && draft?.cameras && typeof draft.cameras === "object") {
    const mode = cameras.mode || "remove";
    const cameraMap = draft.cameras;
    Object.entries(cameraMap).forEach(([cameraUuid, camera]) => {
      if (!camera || camera.kind !== "mounted") return;
      const mountedToDeletedBody =
        camera.mountType === "body" &&
        typeof camera.mountBodyUuid === "string" &&
        deletedBodies.has(camera.mountBodyUuid);
      const mountedToDeletedSite =
        camera.mountType === "site" &&
        typeof camera.mountSiteUuid === "string" &&
        deletedSites.has(camera.mountSiteUuid);
      if (!mountedToDeletedBody && !mountedToDeletedSite) return;

      if (mode === "downgrade") {
        cameraMap[cameraUuid] = {
          ...camera,
          kind: "fixed",
          mountType: undefined,
          mountBodyUuid: undefined,
          mountSiteUuid: undefined,
        };
        return;
      }

      delete cameraMap[cameraUuid];
    });
  }

  if (external?.grasps) {
    const graspActions = getGraspActions();
    deletedBodies.forEach((bodyUuid) => {
      graspActions.clearGraspsForBody(bodyUuid);
    });
  }
}
